import React, { useCallback, useMemo } from "react";
import {
  AirCfarHtsMerchandising,
  useDeviceTypes,
  HtsMobileContinueButton,
} from "halifax";
import { useDispatch, useSelector } from "react-redux";
import { CfarOffer } from "redmond";
import { CfarOfferType } from "redmond/apis/tysons/ancillaries";
import {
  setSelectedCfarId,
  setSelectedHtsCfarOfferId,
} from "../../../actions/actions";
import {
  DO_NOT_APPLY_OPTION_KEY,
  selectedHtsCfarOfferIdSelector,
} from "../../../reducer";
import { isCfarSelectedSelector } from "../../../../book/reducer/selectors";
import { getSelectedAccountReferenceId } from "../../../../rewards/reducer";

export const Cap1AirCfarHtsMerchandising = (props: {
  cfarOffer: CfarOffer;
  onLoadSuccess?: () => void;
  onLoadError?: () => void;
  isLoading: boolean;
  isError: boolean;
  onContinue?: () => void;
  retryCount: number;
}) => {
  const dispatch = useDispatch();
  const { matchesMobile } = useDeviceTypes();
  const isCfarSelected = useSelector(isCfarSelectedSelector);
  const selectedHtsCfarOfferId = useSelector(selectedHtsCfarOfferIdSelector);
  const selectedAccountReferenceId = useSelector(getSelectedAccountReferenceId);

  const {
    cfarOffer,
    onLoadSuccess,
    onLoadError,
    isLoading,
    isError,
    onContinue,
    retryCount,
  } = props;

  const isMobileContinueButtonVisible =
    matchesMobile && !isLoading && (isCfarSelected || isError);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
  };

  const selectNoOffer = useCallback(
    (unselectedHtsCfarOfferIdValue?: string) => {
      dispatch(
        setSelectedCfarId({
          productId: DO_NOT_APPLY_OPTION_KEY,
          policyId: DO_NOT_APPLY_OPTION_KEY,
          quoteId: undefined,
        })
      );
      if (unselectedHtsCfarOfferIdValue) {
        dispatch(setSelectedHtsCfarOfferId(unselectedHtsCfarOfferIdValue));
      }
    },
    [dispatch]
  );

  const { rewardsType, rewardsConversion } = useMemo(() => {
    if (!selectedAccountReferenceId) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    const rewardsPremiumAmount =
      cfarOffer.premiumPerPax.rewards[selectedAccountReferenceId];

    if (
      !rewardsPremiumAmount ||
      rewardsPremiumAmount.value === 0 ||
      cfarOffer.premiumPerPax.fiat.value === 0
    ) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    return {
      rewardsType: rewardsPremiumAmount.currency.toLowerCase(),
      rewardsConversion:
        rewardsPremiumAmount.value / cfarOffer.premiumPerPax.fiat.value,
    };
  }, [cfarOffer, selectedAccountReferenceId]);

  const { merchandisingUrl: merchandisingUrlString, id: cfarId } = cfarOffer;

  const merchandisingUrl = useMemo(() => {
    if (!merchandisingUrlString) {
      return null;
    }

    const url = new URL(merchandisingUrlString);

    if (rewardsType && rewardsConversion) {
      url.searchParams.set("rewardsType", rewardsType);
      url.searchParams.set("rewardsConversion", rewardsConversion.toString());
    }
    if (retryCount > 0) {
      url.searchParams.set("retryCount", retryCount.toString());
    }

    return url;
  }, [merchandisingUrlString, rewardsType, rewardsConversion, retryCount]);

  const handleLoadError = useCallback(() => {
    selectNoOffer();

    if (onLoadError) {
      onLoadError();
    }
  }, [selectNoOffer, onLoadError]);

  if (!merchandisingUrl) {
    return null;
  }

  return (
    <>
      <AirCfarHtsMerchandising
        merchandisingUrl={merchandisingUrl}
        onSelect={(eventData) => {
          if (eventData.attached) {
            dispatch(
              setSelectedCfarId({ ...cfarId, offerType: CfarOfferType.Hts })
            );
            dispatch(setSelectedHtsCfarOfferId(eventData.offer.offerId));
          } else {
            selectNoOffer(eventData.selected);
          }
        }}
        selected={selectedHtsCfarOfferId}
        onLoadSuccess={onLoadSuccess}
        onLoadError={handleLoadError}
        isLoading={isLoading}
        isError={isError}
        offerId={cfarId.productId}
      />
      {isMobileContinueButtonVisible && (
        <HtsMobileContinueButton
          onClick={handleContinue}
          dataTestId="flights-cfar-continue-button"
        />
      )}
    </>
  );
};
